import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Paymentresults.css';

const API_BASE = 'https://mykaddle-api.onrender.com/api/payments';

const PaymentResult = ({ type: propType }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');

  // Yoco may pass: ?provider=yoco&id=pay_xxx or ?status=successful
  const provider = params.get('provider') || 'ozow';
  const yocoPaymentId = params.get('id'); // Yoco checkout ID
  const yocoStatus = params.get('status'); // if using single returnUrl

  // Determine type from URL path OR query params
  const type = propType || (yocoStatus === 'cancelled' ? 'cancel' : yocoStatus === 'successful' ? 'success' : 'error');

  const ref = params.get('TransactionReference') || params.get('TransactionId') || yocoPaymentId || '';
  const amount = params.get('Amount') || '';

  // ── Verify Yoco payment on mount ──
  useEffect(() => {
    // Only verify for Yoco success returns
    if (provider === 'yoco' && type === 'success' && yocoPaymentId) {
      verifyYocoPayment(yocoPaymentId);
    } else {
      setVerifying(false);
      setVerified(true);
    }
  }, [provider, type, yocoPaymentId]);

  const verifyYocoPayment = async (paymentId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/verify-yoco`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ paymentId }),
      });
      const data = await res.json();
      
      if (data.success || data.status === 'successful') {
        setVerified(true);
      } else {
        setVerifyError(data.message || 'Payment verification failed.');
      }
    } catch (err) {
      setVerifyError('Could not verify payment. Contact support if charged.');
    } finally {
      setVerifying(false);
    }
  };

  // Auto-redirect after success
  useEffect(() => {
    if (type === 'success' && verified && !verifying) {
      const t = setTimeout(() => navigate('/tutee/dashboard'), 5000);
      return () => clearTimeout(t);
    }
  }, [type, verified, verifying, navigate]);

  const config = {
    success: {
      icon: '✓',
      color: '#16a34a',
      bg: '#f0fdf4',
      title: verifying ? 'Verifying payment…' : 'Payment successful',
      body: verifying 
        ? 'Please wait while we confirm your payment with Yoco…'
        : `Your subscription is now active. Funds are held in escrow for 48 hours during which you may raise a dispute, then released to your tutor after 7 days.`,
      cta: 'Go to dashboard',
      path: '/tutee/dashboard',
    },
    cancel: {
      icon: '×',
      color: '#d97706',
      bg: '#fffbeb',
      title: 'Payment cancelled',
      body: 'Your payment was cancelled. No charge was made. You can try again from your dashboard.',
      cta: 'Back to dashboard',
      path: '/tutee/dashboard',
    },
    error: {
      icon: '⚠',
      color: '#dc2626',
      bg: '#fef2f2',
      title: 'Payment failed',
      body: 'Something went wrong during payment. No charge was made. Please try again or contact support.',
      cta: 'Back to dashboard',
      path: '/tutee/dashboard',
    },
  }[type];

  if (!config) return null;

  return (
    <div className="pr-page">
      <div className="pr-card">
        <div className="pr-icon" style={{ background: config.bg, color: config.color }}>
          {verifying && type === 'success' ? '⟳' : config.icon}
        </div>
        <h2 className="pr-title">{config.title}</h2>
        <p className="pr-body">
          {verifyError || config.body}
        </p>

        {ref && (
          <p className="pr-ref">Reference: <span>{ref}</span></p>
        )}
        {amount && type === 'success' && (
          <p className="pr-ref">Amount: <span>R {parseFloat(amount).toFixed(2)}</span></p>
        )}

        {type === 'success' && verified && !verifying && (
          <p className="pr-redirect">Redirecting to dashboard in 5 seconds…</p>
        )}

        <button 
          className="pr-btn" 
          onClick={() => navigate(config.path)}
          style={{ background: config.color, opacity: verifying ? 0.6 : 1 }}
          disabled={verifying}
        >
          {verifying ? 'Please wait…' : config.cta}
        </button>
      </div>
    </div>
  );
};

export default PaymentResult;